import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import { wellFlowingTemperatureForMD } from "@/libs/calculations/pressureDropCorrelations";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";
import type { GasSeparationEfficiencies } from "../../fluidPropertiesPipelineTypes";
import type { FluidPropertiesPipelineInputs } from "../types";

// Fluid properties at the pump intake, before and after all gas separation.
export const calculatePumpIntakeFluidProperties = (
  inputs: FluidPropertiesPipelineInputs,
  prev: {
    wellFlowingPressureAtIntakePSIA: number;
    fluidPropertiesAtIntakeContext: FluidPropertiesContext;
    gasSeparationEfficiencies: GasSeparationEfficiencies;
  }
) => {
  const {
    basicData,
    correlations,
    desiredFlowRates,
    bubblePointPressurePSIA,
    condensateApi,
    dewPointPressurePSIA,
    customFluidProperties,
    customViscosityProperties,
    fluidPropertiesOverrides,
    bottomOfPumpsMDFt,
    intakeCrossSectionFt2,
    effectiveCasingInteriorDiameterAtIntakeIn
  } = inputs;
  const {
    wellFlowingPressureAtIntakePSIA,
    fluidPropertiesAtIntakeContext,
    gasSeparationEfficiencies
  } = prev;

  // Seems wrong to set pressure at pump to pressure at intake but it's what
  // we've been doing
  const wellFlowingPressureAtPumpPSIA = wellFlowingPressureAtIntakePSIA;

  // Pump Temperature (downhole temp where pump is located)
  const downholePumpTemperatureF = wellFlowingTemperatureForMD(
    bottomOfPumpsMDFt,
    basicData.datumPointFt,
    basicData.bottomholeTemperatureF,
    basicData.surfaceTemperatureF
  );

  const fluidPropertiesAtPumpIntakeContext = getFluidPropertiesContext(
    basicData,
    correlations,
    wellFlowingPressureAtPumpPSIA,
    downholePumpTemperatureF,
    effectiveCasingInteriorDiameterAtIntakeIn,
    desiredFlowRates,
    fluidPropertiesAtIntakeContext.flowRatesInSitu,
    fluidPropertiesAtIntakeContext.flowRatePrerequisites,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    intakeCrossSectionFt2,
    gasSeparationEfficiencies.combinedGasSeparationEfficiencyRatio,
    fluidPropertiesOverrides,
    condensateApi,
    true
  );

  const fluidPropertiesAfterAllGasSeparationAtPumpContext =
    getFluidPropertiesContext(
      basicData,
      correlations,
      wellFlowingPressureAtPumpPSIA,
      downholePumpTemperatureF,
      effectiveCasingInteriorDiameterAtIntakeIn,
      desiredFlowRates,
      fluidPropertiesAtPumpIntakeContext.flowRatesInSitu,
      fluidPropertiesAtPumpIntakeContext.flowRatePrerequisites,
      customFluidProperties,
      customViscosityProperties,
      bubblePointPressurePSIA,
      dewPointPressurePSIA,
      intakeCrossSectionFt2,
      0,
      fluidPropertiesOverrides,
      condensateApi,
      true
    );

  return {
    wellFlowingPressureAtPumpPSIA,
    downholePumpTemperatureF,
    fluidPropertiesAtPumpIntakeContext,
    fluidPropertiesAfterAllGasSeparationAtPumpContext
  };
};
